import React, { useState, useEffect } from 'react';
import { Bell, AlertTriangle, Newspaper, Gift, Save, CheckCircle, Smartphone } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { api } from '../services/api';
import { toast } from 'sonner';
import { usePushNotifications } from '../hooks/usePushNotifications';

const DEFAULTS = {
  alertas_criticos: true,  // sempre on; só visualização
  novidades: true,
  promocoes: false,
};

export default function NotificationPreferencesSection({ user, refreshUser }) {
  const initial = { ...DEFAULTS, ...(user?.notification_preferences || {}) };
  const [prefs, setPrefs] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const push = usePushNotifications();
  const [pushBusy, setPushBusy] = useState(false);
  const dirty = JSON.stringify(prefs) !== JSON.stringify(initial);

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (push.isSubscribed) {
        await push.unsubscribe();
        toast.success('Notificações no celular desativadas');
      } else {
        const ok = await push.subscribe(user?.municipio_id || '');
        if (ok) {
          toast.success('Notificações no celular ativadas');
        } else if (push.permission === 'denied') {
          toast.error('Permissão negada. Habilite nas configurações do navegador.');
        } else {
          toast.error('Não foi possível ativar notificações');
        }
      }
    } finally {
      setPushBusy(false);
    }
  };

  // Re-sync when user prop updates
  useEffect(() => {
    setPrefs({ ...DEFAULTS, ...(user?.notification_preferences || {}) });
  }, [user]);

  const onSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await api.put('/auth/profile', { notification_preferences: prefs });
      if (refreshUser) await refreshUser();
      setSaved(true);
      toast.success('Preferências salvas!');
      setTimeout(() => setSaved(false), 3500);
    } catch (e) {
      toast.error('Erro ao salvar: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const Row = ({ id, icon: Icon, title, desc, value, onChange, locked, badge }) => (
    <div className="flex items-start justify-between gap-4 py-4 border-b last:border-0">
      <div className="flex items-start gap-3 flex-1">
        <div className="w-10 h-10 rounded-lg bg-violet-50 flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5 text-violet-600" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <p className="font-medium text-[var(--text-primary)]">{title}</p>
            {badge && <Badge variant="secondary" className="text-xs">{badge}</Badge>}
          </div>
          <p className="text-sm text-[var(--text-secondary)] mt-0.5">{desc}</p>
        </div>
      </div>
      <Switch
        checked={value}
        onCheckedChange={onChange}
        disabled={locked}
        data-testid={`notif-pref-${id}`}
      />
    </div>
  );

  return (
    <div data-testid="notification-preferences-section">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-violet-600" />
            Preferências de Notificação
          </CardTitle>
          <CardDescription>
            Escolha o que quer receber por e-mail. Alertas críticos (senha, verificação, decisões sobre seu negócio) são sempre enviados.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Row
            id="alertas-criticos"
            icon={AlertTriangle}
            title="Alertas críticos"
            desc="Recuperação de senha, OTP, aprovação/reprovação de negócio, denúncias e respostas a solicitações LGPD."
            value={prefs.alertas_criticos}
            onChange={() => {}}
            locked
            badge="Obrigatório"
          />
          <Row
            id="novidades"
            icon={Newspaper}
            title="Novidades e eventos"
            desc="Novas notícias, programas e ações do município. (In-app continua sempre ativo)"
            value={prefs.novidades}
            onChange={(v) => setPrefs(p => ({ ...p, novidades: v }))}
          />
          <Row
            id="promocoes"
            icon={Gift}
            title="Promoções e campanhas"
            desc="Cupons, ofertas relâmpago e campanhas de marketing do município."
            value={prefs.promocoes}
            onChange={(v) => setPrefs(p => ({ ...p, promocoes: v }))}
          />

          {/* Push notifications (PWA) — canal complementar ao email */}
          {push.isSupported && (
            <div
              className="flex items-start justify-between py-4 border-t border-gray-100 mt-2"
              data-testid="push-pref-row"
            >
              <div className="flex items-start gap-3 flex-1">
                <Smartphone className="w-5 h-5 text-violet-600 mt-0.5 shrink-0" />
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-sm">Notificações no celular (push)</p>
                    {push.isSubscribed && (
                      <Badge variant="outline" className="text-emerald-700 border-emerald-200 bg-emerald-50">
                        Ativado neste dispositivo
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-[var(--text-secondary)] mt-1">
                    Receba alertas em tempo real mesmo com o app fechado. Funciona melhor depois de instalar o PWA.
                  </p>
                  {push.permission === 'denied' && (
                    <p className="text-xs text-red-600 mt-1">
                      Permissão negada — habilite nas configurações do navegador.
                    </p>
                  )}
                </div>
              </div>
              <Switch
                checked={push.isSubscribed}
                onCheckedChange={togglePush}
                disabled={pushBusy || push.loading || push.permission === 'denied'}
                data-testid="push-pref-toggle"
              />
            </div>
          )}

          <div className="mt-6 flex items-center justify-between">
            <p className="text-xs text-[var(--text-secondary)]">
              {prefs.novidades || prefs.promocoes
                ? '✉️ Você receberá emails além dos críticos.'
                : 'ℹ️ Você só receberá emails críticos (senha, OTP, jurídico).'}
            </p>
            <div className="flex items-center gap-2">
              {saved && (
                <span className="text-emerald-600 text-sm flex items-center gap-1" data-testid="notif-prefs-saved">
                  <CheckCircle className="w-4 h-4" /> Salvo
                </span>
              )}
              <Button
                onClick={onSave}
                disabled={!dirty || saving}
                data-testid="notif-prefs-save"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Salvando…' : 'Salvar preferências'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
